import { inject, Injectable } from '@angular/core';
import { ToasterService } from '@maersk-global/angular-shared-library';
import { BehaviorSubject, catchError, map, Observable, throwError } from 'rxjs';
import { SYNC_ACCESS_CUSTOMER_NOT_AVAILABLE } from '../constants/constants';
import { FeatureAccessType, Roles, ShipmentAccessType } from '../constants/roles-permissions';
import { RbacState, SyncAccessRequest, SyncAccessResponse } from '../models/payload';
import { StorageService } from './storage.service';
import { UserDetailsService } from './user-details.service';

@Injectable({ providedIn: 'root' })
export class RbacService {

  private rbacStateSubject = new BehaviorSubject<RbacState | null>(null);
  rbacState$: Observable<RbacState | null> = this.rbacStateSubject.asObservable();

  private userDetailsService = inject(UserDetailsService);
  private storageService = inject(StorageService);
  private toasterService = inject(ToasterService);

  constructor() {
    const stored = this.storageService.getRbacState();
    if (stored) {
      this.rbacStateSubject.next(stored);
    }
  }

  syncAccess(request: SyncAccessRequest): Observable<RbacState> {
    return this.userDetailsService.syncAccess(request).pipe(
      map((response: SyncAccessResponse) => {
        const state: RbacState = {
          userId: response.user_id,
          role: response.role,
          customerNumber: response.customer_access.customer_number,
          customerName: response.customer_access.customer_name,
          shipmentAccessType: response.customer_access.shipment_access_type,
          featureAccessType: response.customer_access.feature_access_type,
          accessSource: response.customer_access.access_source,
        };
        this.storageService.setRbacState(state);
        this.rbacStateSubject.next(state);
        return state;
      }),
      catchError((error) => {
        if (error?.status === 403) {
          this.toasterService.showToast({
            message: SYNC_ACCESS_CUSTOMER_NOT_AVAILABLE,
            type: 'error',
            position: 'top-center',
          });
        }
        return throwError(() => error);
      })
    );
  }

  /** True when the user holds the global ADMIN role (super admin — bypasses all customer-scoped restrictions). */
  isGlobalAdmin(): boolean {
    return this.rbacStateSubject.value?.role === Roles.ROLE_ADMIN;
  }

  /**
   * Sync Access RBAC rule for customer Settings:
   * allow when role === 'ADMIN' OR customerAccess.featureAccessType === 'ADMIN'.
   */
  hasAdminFeatureAccess(): boolean {
    const state = this.rbacStateSubject.value;
    if (!state) return false;
    return state.role === Roles.ROLE_ADMIN || state.featureAccessType === FeatureAccessType.ADMIN;
  }

  /** True when the user may access settings/admin screens. */
  canAccessSettings(): boolean {
    return this.hasAdminFeatureAccess();
  }

  /** Shipment access tier stored for future backend filter enforcement. */
  getShipmentAccessType(): ShipmentAccessType | null {
    return this.rbacStateSubject.value?.shipmentAccessType ?? null;
  }

  getCurrentState(): RbacState | null {
    return this.rbacStateSubject.value;
  }

  clearState(): void {
    this.storageService.clearRbacState();
    this.rbacStateSubject.next(null);
  }
}
